import { ChangeDetectionStrategy, Component, signal, computed, inject, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { TabsModule, TabPanels } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { Skeleton } from 'primeng/skeleton';
import { debounceTime, switchMap, catchError, map } from 'rxjs/operators';
import { Subject, of, from } from 'rxjs';

import { AuthService } from '../../../core/services/auth';
import { GlobalSearchService, PatientSearchResult, InvoiceSearchResult, AppointmentSearchResult } from './global-search-data';
import { TranslateModule } from '@ngx-translate/core';
import { MobileTabSelectorComponent } from '../tab-shell/mobile-tab-selector';

export interface SearchItem {
  label: string;
  description: string;
  icon: string;
  route: string;
  category: string;
}

const NAVIGATION_ITEMS: SearchItem[] = [
  { label: 'Mon tableau de bord', description: 'Vue générale', icon: 'pi pi-th-large', route: '/dashboard', category: 'Navigation' },
  { label: 'Analytique', description: 'KPIs et graphiques', icon: 'pi pi-chart-bar', route: '/reports?tab=analytique', category: 'Navigation' },
  { label: 'File d’attente patients', description: 'Patients en attente de prise en charge', icon: 'pi pi-list-check', route: '/reception-hub?tab=file', category: 'Navigation' },
  { label: 'Pré-enregistrements', description: 'Patients à confirmer', icon: 'pi pi-user-plus', route: '/reception-hub?tab=pre-enregistrements', category: 'Patients' },
  { label: 'Salles d’attente', description: 'Occupation et configuration', icon: 'pi pi-building', route: '/reception-hub?tab=salles', category: 'Patients' },
  { label: 'Patients', description: 'Liste des patients', icon: 'pi pi-users', route: '/patients', category: 'Patients' },
  { label: 'Nouveau patient', description: 'Ajouter un patient', icon: 'pi pi-user-plus', route: '/patients/new', category: 'Patients' },
  { label: 'Rendez-vous', description: 'Gestion des RDV', icon: 'pi pi-calendar', route: '/appointments', category: 'Consultations' },
  { label: 'Consultations', description: 'Dossiers de consultation', icon: 'pi pi-file', route: '/consultations-hub?tab=cabinet', category: 'Consultations' },
  { label: 'Prescriptions', description: 'Ordonnances', icon: 'pi pi-receipt', route: '/consultations-hub?tab=ordonnances', category: 'Consultations' },
  { label: 'Références médicales', description: 'Suivi des orientations', icon: 'pi pi-directions-alt', route: '/referrals', category: 'Consultations' },
  { label: 'Maladies chroniques', description: 'Suivi populationnel', icon: 'pi pi-heart', route: '/patients?view=chronique', category: 'Consultations' },
  { label: 'Carnets de santé', description: 'Générer et retrouver un carnet patient', icon: 'pi pi-book', route: '/e-carnets', category: 'Documents' },
  { label: 'Arrêts maladie', description: 'Documents d’arrêt patient', icon: 'pi pi-file', route: '/consultations-hub?tab=arrets', category: 'Documents' },
  { label: 'Documents', description: 'GED clinique, documentation et bibliothèque', icon: 'pi pi-folder-open', route: '/ged-hub', category: 'Documents' },
  { label: 'Formulaires', description: 'Formulaires et réponses', icon: 'pi pi-file-edit', route: '/forms', category: 'Documents' },
  { label: 'Laboratoire', description: 'Demandes d\'analyse', icon: 'pi pi-search-plus', route: '/laboratory-hub?tab=demandes', category: 'Examens' },
  { label: 'Résultats de labo', description: 'Résultats et validation des analyses', icon: 'pi pi-file-check', route: '/laboratory-hub?tab=resultats', category: 'Examens' },
  { label: 'Catalogue de tests', description: 'Tests et tarifs du laboratoire', icon: 'pi pi-list-check', route: '/laboratory-hub?tab=catalogue', category: 'Examens' },
  { label: 'Imagerie médicale', description: 'Radios et scanners', icon: 'pi pi-image', route: '/radiology', category: 'Examens' },
  { label: 'Terminologie CIM-11', description: 'Rechercher un diagnostic ou un code CIM-11', icon: 'pi pi-book', route: '/icd11-terminology', category: 'Examens' },
  { label: 'Réactifs laboratoire', description: 'Stock et alertes de réactifs', icon: 'pi pi-box', route: '/laboratory-hub?tab=reactifs', category: 'Examens' },
  { label: 'Prestataires laboratoire', description: 'Partenaires de sous-traitance', icon: 'pi pi-briefcase', route: '/laboratory-hub?tab=partenaires', category: 'Examens' },
  { label: 'Facturation', description: 'Factures et paiements', icon: 'pi pi-credit-card', route: '/billing-hub?tab=factures', category: 'Finance' },
  { label: 'Tarifs', description: 'Tarification des services', icon: 'pi pi-tag', route: '/billing-hub?tab=tarifs', category: 'Finance' },
  { label: 'Consommation', description: 'Quotas et forfait', icon: 'pi pi-gauge', route: '/usage', category: 'Finance' },
  { label: 'Devis', description: 'Propositions tarifaires', icon: 'pi pi-file-edit', route: '/billing-hub?tab=devis', category: 'Finance' },
  { label: 'Tiers-payant', description: 'Demandes et remboursements assurance', icon: 'pi pi-shield', route: '/insurance-hub?tab=tierspayant', category: 'Finance' },
  { label: 'Transactions', description: 'Historique des encaissements', icon: 'pi pi-arrow-right-arrow-left', route: '/accounting-hub?tab=ecritures', category: 'Finance' },
  { label: 'Historique des ventes', description: 'Ventes de la caisse pharmacie', icon: 'pi pi-receipt', route: '/pharmacy-pos?tab=historique', category: 'Pharmacie' },
  { label: 'Prévisions de stock', description: 'Ruptures et réapprovisionnement', icon: 'pi pi-chart-line', route: '/stock-hub?tab=previsions', category: 'Pharmacie' },
  { label: 'Achats pharmacie', description: 'Bons de commande fournisseurs', icon: 'pi pi-file-edit', route: '/pharmacy-purchase-orders', category: 'Pharmacie' },
  { label: 'Fournisseurs pharmacie', description: 'Référentiel fournisseurs', icon: 'pi pi-truck', route: '/pharmacy-suppliers', category: 'Pharmacie' },
  { label: 'Banque de sang', description: 'Stocks et compatibilités', icon: 'pi pi-heart', route: '/blood-hub?tab=banque', category: 'Soins' },
  { label: 'Caisse POS', description: 'Point de vente pharmacie', icon: 'pi pi-shopping-cart', route: '/pharmacy-pos', category: 'Pharmacie' },
  { label: 'Ambulances', description: 'Flotte ambulances', icon: 'pi pi-car', route: '/fleet?tab=vehicles', category: 'Urgences' },
  { label: 'Équipement ambulance', description: 'Inspections et disponibilité', icon: 'pi pi-box', route: '/fleet?tab=equipment', category: 'Urgences' },
  { label: 'Maintenance ambulance', description: 'Interventions planifiées et en retard', icon: 'pi pi-wrench', route: '/fleet?tab=maintenance', category: 'Urgences' },
  { label: 'Urgences', description: 'Appels d\'urgence', icon: 'pi pi-exclamation-triangle', route: '/emergency-regulation', category: 'Urgences' },
  { label: 'Médecins', description: 'Corps médical', icon: 'pi pi-user-plus', route: '/people-hub?tab=personnel&role=doctor', category: 'Équipe' },
  { label: 'Personnel', description: 'Gestion RH', icon: 'pi pi-id-card', route: '/people-hub?tab=personnel', category: 'Équipe' },
  { label: 'Plannings', description: 'Gardes et plannings', icon: 'pi pi-calendar', route: '/shifts', category: 'Équipe' },
  { label: 'Hospitalisations', description: 'Admissions', icon: 'pi pi-building', route: '/hospitalization', category: 'Hospitalisation' },
  { label: 'Gestion des lits', description: 'Occupation des lits', icon: 'pi pi-sliders-v', route: '/beds', category: 'Hospitalisation' },
  { label: 'Équipements chirurgicaux', description: 'Maintenance et stérilisation', icon: 'pi pi-cog', route: '/bloc-hub?tab=equipements', category: 'Hospitalisation' },
  { label: 'Rapports', description: 'Rapports et exports', icon: 'pi pi-chart-line', route: '/reports', category: 'Pilotage' },
  { label: 'Réclamations', description: 'Suivi des plaintes', icon: 'pi pi-comments', route: '/complaints', category: 'Pilotage' },
  { label: 'Paramètres', description: 'Configuration générale', icon: 'pi pi-cog', route: '/organization?section=identity', category: 'Administration' },
  { label: 'Rôles et permissions', description: 'Gestion des accès', icon: 'pi pi-shield', route: '/roles', category: 'Administration' },
  { label: 'Journal d\'audit', description: 'Traçabilité des actions', icon: 'pi pi-eye', route: '/integrations-hub?tab=audit', category: 'Administration' },
  { label: 'Sauvegardes', description: 'Sauvegardes et restauration', icon: 'pi pi-server', route: '/integrations-hub?tab=sauvegardes', category: 'Administration' },
  { label: 'Maternité', description: 'Tableau de bord maternité', icon: 'pi pi-home', route: '/maternity-hub?tab=tableau', category: 'Maternité' },
  { label: 'Grossesses', description: 'Suivi des grossesses', icon: 'pi pi-heart-fill', route: '/maternity-hub?tab=grossesses', category: 'Maternité' },
  { label: 'Vaccinations', description: 'Carnet vaccinal', icon: 'pi pi-shield', route: '/vaccinations-hub?tab=actes', category: 'Soins' },
  { label: 'Assurances', description: 'Assureurs partenaires', icon: 'pi pi-shield', route: '/insurance-hub?tab=organismes', category: 'Finance' },
  { label: 'Congés', description: 'Demandes de congé', icon: 'pi pi-calendar-times', route: '/leave-requests', category: 'Équipe' },
  { label: 'Organisation', description: 'Multi-établissements', icon: 'pi pi-building', route: '/organization', category: 'Administration' },
  { label: 'Identité de marque', description: 'Logo, couleurs et CSS avancé', icon: 'pi pi-palette', route: '/organization?section=brand', category: 'Administration' },
  { label: 'Clés API', description: 'Accès des intégrations', icon: 'pi pi-key', route: '/integrations-hub?tab=cles', category: 'Administration' },
  { label: 'Webhooks', description: 'Livraisons des intégrations', icon: 'pi pi-link', route: '/integrations-hub?tab=webhooks', category: 'Administration' },
  { label: 'Messages sécurisés', description: 'Boîte de réception interne', icon: 'pi pi-envelope', route: '/secure-messages', category: 'Administration' },
  { label: 'Alertes opérationnelles', description: 'Alertes ouvertes et priorités', icon: 'pi pi-bell', route: '/alerts', category: 'Pilotage' },
];

const ALL_STAFF = ['admin', 'super_admin', 'doctor', 'receptionist', 'laborantin', 'pharmacienne', 'aide_soignante', 'gestionnaire'];
const DOCUMENT_USERS = ['admin', 'super_admin', 'doctor', 'receptionist', 'pharmacienne'];
const ADMIN = ['admin', 'super_admin'];
const CLINICAL = ['admin', 'super_admin', 'doctor', 'aide_soignante'];
const DOCTOR = ['admin', 'super_admin', 'doctor'];
const LAB = ['admin', 'super_admin', 'doctor', 'laborantin'];
const FINANCE = ['admin', 'super_admin', 'gestionnaire'];
const PHARMACY = ['admin', 'super_admin', 'pharmacienne'];
const LOGISTICS = ['admin', 'super_admin', 'receptionist', 'gestionnaire'];
const FLEET_MGMT = ['admin', 'super_admin', 'gestionnaire'];

const ROUTE_ACCESS: Record<string, string[]> = {
  '/dashboard': ALL_STAFF,
  '/reports?tab=analytique': ['admin', 'super_admin'],
  '/reception-hub?tab=file': ['admin', 'super_admin', 'doctor', 'receptionist'],
  '/reception-hub?tab=pre-enregistrements': ['admin', 'super_admin', 'doctor', 'receptionist'],
  '/reception-hub?tab=salles': ADMIN,
  '/patients': ['admin', 'super_admin', 'doctor', 'receptionist', 'gestionnaire', 'aide_soignante'],
  '/patients/new': ['admin', 'super_admin', 'doctor', 'receptionist'],
  '/appointments': ['admin', 'super_admin', 'doctor', 'receptionist', 'gestionnaire'],
  '/consultations-hub?tab=cabinet': DOCTOR,
  '/consultations-hub?tab=ordonnances': ['admin', 'super_admin', 'doctor', 'pharmacienne'],
  '/referrals': ['admin', 'super_admin', 'doctor'],
  '/patients?view=chronique': CLINICAL,
  '/e-carnets': DOCTOR,
  '/consultations-hub?tab=arrets': DOCTOR,
  '/ged-hub': DOCUMENT_USERS,
  '/forms': ['admin', 'super_admin', 'receptionist', 'doctor'],
  '/laboratory-hub?tab=demandes': LAB,
  '/laboratory-hub?tab=resultats': LAB,
  '/laboratory-hub?tab=catalogue': LAB,
  '/radiology': LAB,
  '/icd11-terminology': LAB,
  '/laboratory-hub?tab=reactifs': ['admin', 'super_admin', 'laborantin'],
  '/laboratory-hub?tab=partenaires': ['admin', 'super_admin', 'laborantin', 'gestionnaire'],
  '/billing-hub?tab=factures': FINANCE,
  '/billing-hub?tab=devis': FINANCE,
  '/billing-hub?tab=tarifs': FINANCE,
  '/usage': FINANCE,
  '/insurance-hub?tab=tierspayant': FINANCE,
  '/accounting-hub?tab=ecritures': FINANCE,
  '/pharmacy-pos?tab=historique': PHARMACY,
  '/stock-hub?tab=previsions': ['admin', 'super_admin', 'pharmacienne', 'gestionnaire'],
  '/pharmacy-purchase-orders': ['admin', 'super_admin', 'pharmacienne', 'gestionnaire'],
  '/pharmacy-suppliers': ['admin', 'super_admin', 'pharmacienne', 'gestionnaire'],
  '/pharmacy-pos': PHARMACY,
  '/blood-hub?tab=banque': LAB,
  '/fleet?tab=vehicles': LOGISTICS,
  '/fleet': LOGISTICS,
  '/fleet?tab=equipment': FLEET_MGMT,
  '/fleet?tab=maintenance': FLEET_MGMT,
  '/emergency-regulation': ['admin', 'super_admin', 'doctor', 'receptionist', 'gestionnaire'],
  '/people-hub?tab=personnel&role=doctor': ADMIN,
  '/people-hub?tab=personnel': ADMIN,
  '/shifts': ADMIN,
  '/hospitalization': CLINICAL,
  '/beds': CLINICAL,
  '/bloc-hub?tab=equipements': DOCTOR,
  '/reports': ['admin', 'super_admin', 'doctor', 'gestionnaire'],
  '/complaints': ['admin', 'super_admin', 'receptionist'],
  '/organization?section=identity': ADMIN,
  '/roles': ADMIN,
  '/integrations-hub?tab=audit': ADMIN,
  '/integrations-hub?tab=sauvegardes': ADMIN,
  '/maternity-hub?tab=tableau': CLINICAL,
  '/maternity-hub?tab=grossesses': CLINICAL,
  '/vaccinations-hub?tab=actes': CLINICAL,
  '/insurance-hub?tab=organismes': FINANCE,
  '/leave-requests': ADMIN,
  '/organization': ADMIN,
  '/organization?section=brand': ADMIN,
  '/integrations-hub?tab=cles': ADMIN,
  '/integrations-hub?tab=webhooks': ADMIN,
  '/secure-messages': ALL_STAFF,
  '/alerts': ['admin', 'super_admin', 'doctor', 'receptionist'],
};

@Component({
  selector: 'app-global-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onGlobalKeydown($event)'
  },
  imports: [Skeleton, 
    CommonModule,
    FormsModule,
    Dialog,
    InputText,
    RippleModule,
    TabsModule,
    TabPanels,
    TagModule,
    BadgeModule, TranslateModule, MobileTabSelectorComponent],
  styles: [`@media (max-width: 767px) { p-tablist { display: none; } }`],
  template: `
    <p-dialog appendTo="body"
      [visible]="visible()"
      (visibleChange)="onVisibleChange($event)"
      (onShow)="focusSearchInput()"
      [modal]="true"
      [style]="{ width: '700px', maxWidth: '95vw' }"
      [showHeader]="false"
      [pt]="{ root: { 'aria-labelledby': 'global-search-title' } }"
      [focusOnShow]="false"
      [dismissableMask]="true"
      styleClass="!rounded-2xl !overflow-hidden !p-0 !shadow-2xl">

      <div class="p-0">
        <!-- Search input -->
        <label id="global-search-title" for="global-search-query" class="block px-4 pt-3 text-sm font-semibold text-ink">Recherche globale</label>
        <div class="flex items-center gap-3 px-4 py-3 border-b border-hairline">
          <i class="pi pi-search text-ink-muted text-lg flex-shrink-0"></i>
          <input
            #searchInput
            id="global-search-query"
            type="text"
            pInputText
            [placeholder]="'GLOBAL_SEARCH.PLACEHOLDER' | translate"
            class="flex-1 !border-none !shadow-none !outline-none text-ink bg-transparent text-base"
            aria-label="Recherche globale"
            autocomplete="off"
            spellcheck="false"
            [(ngModel)]="query"
            (ngModelChange)="onSearch($event)"
            (keydown)="onKeydown($event)" />
          @if (query.length > 0) {
            <p-badge [value]="totalResultsStr()" styleClass="!bg-primary-surface !text-primary-text" class="flex-shrink-0"></p-badge>
          }
          <span class="text-xs text-ink-muted bg-chrome px-2 py-1 rounded font-mono flex-shrink-0">ESC</span>
        </div>

        <!-- Tab navigation -->
        <div class="px-4 pt-3">
          <app-mobile-tab-selector [label]="'GLOBAL_SEARCH.VIEWS' | translate" [tabs]="searchTabs"
            [labels]="{ all: ('COMMON.ALL' | translate), navigation: ('GLOBAL_SEARCH.NAVIGATION' | translate), data: ('GLOBAL_SEARCH.DATA' | translate) }"
            [active]="activeTab" (changed)="onTabChange($event)" />
        </div>
        <p-tabs [(value)]="activeTab" (valueChange)="onTabChange($event)" styleClass="w-full !border-b">
          <p-tablist class="w-full flex">
            <p-tab value="all">
              {{ 'COMMON.ALL' | translate }}
              <ng-template pTemplate="headerContent">
                <span class="flex items-center gap-1">
                  <i class="pi pi-th-large text-xs"></i>
                  <span>{{ 'COMMON.ALL' | translate }}</span>
                </span>
              </ng-template>
            </p-tab>
            <p-tab value="navigation">
              Navigation
              <ng-template pTemplate="headerContent">
                <span class="flex items-center gap-1">
                  <i class="pi pi-compass text-xs"></i>
                  <span>Navigation</span>
                  @if (navigationResults().length > 0) {
                    <span class="ml-1 text-[10px] bg-primary-surface text-primary-text px-1.5 rounded-full">
                      {{ navigationResults().length }}
                    </span>
                  }
                </span>
              </ng-template>
            </p-tab>
            <p-tab value="data">
              {{ 'GLOBAL_SEARCH.DATA' | translate }}
              <ng-template pTemplate="headerContent">
                <span class="flex items-center gap-1">
                  <i class="pi pi-database text-xs"></i>
                  <span>{{ 'GLOBAL_SEARCH.DATA' | translate }}</span>
                  @if (dataResults().length > 0) {
                    <span class="ml-1 text-[10px] bg-primary-surface text-primary-text px-1.5 rounded-full">
                      {{ dataResults().length }}
                    </span>
                  }
                </span>
              </ng-template>
            </p-tab>
          </p-tablist>

          <p-tabpanels>
          <!-- All results tab -->
          <p-tabpanel value="all">
            @if (searchLoading()) {
              <div class="flex flex-col gap-2 py-2" role="status" aria-label="Recherche en cours">
              @for (n of [1, 2, 3, 4]; track n) {
                <p-skeleton height="3rem" borderRadius="0.75rem" />
              }
            </div>
            } @else if (allResults().length === 0 && query.length > 1) {
              <div class="px-4 py-16 text-center text-ink-muted">
                <i class="pi pi-search text-4xl mb-4 opacity-30"></i>
                <p class="text-lg font-medium">Aucun résultat pour « {{ query }} »</p>
                <p class="text-sm mt-2">{{ 'GLOBAL_SEARCH.TRY_ANOTHER' | translate }}</p>
              </div>
            } @else if (query.length === 0) {
              <div class="px-4 py-8">
                <p class="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-3">{{ 'GLOBAL_SEARCH.QUICK_ACCESS' | translate }}</p>
              </div>
            }

            @if (query.length === 0) {
              @for (item of quickAccess(); track item.route; let i = $index) {
                <button
                  class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-chrome transition-colors text-left group"
                  [class.bg-primary-surface]="selectedIndex() === i"
                  (click)="navigate(item.route)"
                  pRipple>
                  <div class="w-9 h-9 rounded-lg bg-chrome group-hover:bg-primary-surface flex items-center justify-center flex-shrink-0 transition-colors">
                    <i [class]="item.icon + ' text-ink-muted group-hover:text-primary-text text-sm transition-colors'"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-ink truncate">{{ item.label }}</p>
                    <p class="text-xs text-ink-muted truncate">{{ item.description }}</p>
                  </div>
                  <span class="text-xs text-ink-muted bg-chrome border border-hairline px-1.5 py-0.5 rounded text-nowrap flex-shrink-0">
                    {{ item.category }}
                  </span>
                </button>
              }
            }

            @if (query.length > 0) {
              @for (result of allResults(); track result.id; let i = $index) {
                @if (result.type === 'navigation') {
                  <button
                    class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-chrome transition-colors text-left group"
                    [class.bg-primary-surface]="selectedIndex() === i"
                    (click)="navigate(result.route)"
                    pRipple>
                    <div class="w-9 h-9 rounded-lg bg-chrome group-hover:bg-primary-surface flex items-center justify-center flex-shrink-0 transition-colors">
                      <i [class]="result.icon + ' text-ink-muted group-hover:text-primary-text text-sm transition-colors'"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-ink truncate">{{ result.name }}</p>
                      <p class="text-xs text-ink-muted truncate">{{ result.subtitle }}</p>
                    </div>
                    <p-tag value="Navigation" severity="secondary" [styleClass]="'!text-[10px] !px-2'"></p-tag>
                  </button>
                } @else if (result.type === 'patient') {
                  <button
                    class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary-surface transition-colors text-left group"
                    [class.bg-primary-surface]="selectedIndex() === i"
                    (click)="navigate('/patients/' + result.id)"
                    pRipple>
                    <div class="w-9 h-9 rounded-lg bg-primary-surface group-hover:bg-primary-surface flex items-center justify-center flex-shrink-0 transition-colors">
                      <i class="pi pi-user text-primary-text group-hover:text-primary-text text-sm transition-colors"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-ink truncate">{{ result.name }}</p>
                      <p class="text-xs text-ink-muted truncate">{{ result.subtitle }}</p>
                    </div>
                    <p-tag value="Patient" severity="success" [styleClass]="'!text-[10px] !px-2'"></p-tag>
                  </button>
                } @else if (result.type === 'invoice') {
                  <button
                    class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-success-surface transition-colors text-left group"
                    [class.bg-primary-surface]="selectedIndex() === i"
                    (click)="navigate('/billing/' + result.id)"
                    pRipple>
                    <div class="w-9 h-9 rounded-lg bg-success-surface group-hover:bg-success-surface flex items-center justify-center flex-shrink-0 transition-colors">
                      <i class="pi pi-receipt text-success-text group-hover:text-success-text text-sm transition-colors"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-ink truncate">{{ result.name }}</p>
                      <p class="text-xs text-ink-muted truncate">{{ result.subtitle }}</p>
                    </div>
                    <p-tag value="Facture" severity="info" [styleClass]="'!text-[10px] !px-2'"></p-tag>
                  </button>
                } @else if (result.type === 'appointment') {
                  <button
                    class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-warning-surface transition-colors text-left group"
                    [class.bg-primary-surface]="selectedIndex() === i"
                    (click)="navigate('/appointments')"
                    pRipple>
                    <div class="w-9 h-9 rounded-lg bg-warning-surface group-hover:bg-warning-surface flex items-center justify-center flex-shrink-0 transition-colors">
                      <i class="pi pi-calendar text-warning-text group-hover:text-warning-text text-sm transition-colors"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-ink truncate">{{ result.name }}</p>
                      <p class="text-xs text-ink-muted truncate">{{ result.subtitle }}</p>
                    </div>
                    <p-tag value="RDV" severity="warning" [styleClass]="'!text-[10px] !px-2'"></p-tag>
                  </button>
                }
              }
            }
          </p-tabpanel>

          <!-- Navigation tab -->
          <p-tabpanel value="navigation">
            @if (navigationResults().length === 0 && query.length > 0) {
              <div class="px-4 py-8 text-center text-ink-muted">
                <i class="pi pi-compass text-3xl opacity-30"></i>
                <p class="text-sm mt-2">{{ 'GLOBAL_SEARCH.NO_NAVIGATION_RESULT' | translate }}</p>
              </div>
            }
            @for (item of navigationResults(); track item.route; let i = $index) {
              <button
                class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-chrome transition-colors text-left"
                [class.bg-primary-surface]="selectedIndex() === i"
                (click)="navigate(item.route)"
                pRipple>
                <div class="w-9 h-9 rounded-lg bg-chrome flex items-center justify-center flex-shrink-0">
                  <i [class]="item.icon + ' text-ink-muted text-sm'"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-ink truncate">{{ item.label }}</p>
                  <p class="text-xs text-ink-muted truncate">{{ item.description }}</p>
                </div>
              </button>
            }
          </p-tabpanel>

          <!-- Data tab -->
          <p-tabpanel value="data">
            @if (searchLoading()) {
              <div class="flex flex-col gap-2 py-2" role="status" aria-label="Recherche en cours">
              @for (n of [1, 2, 3, 4]; track n) {
                <p-skeleton height="3rem" borderRadius="0.75rem" />
              }
            </div>
            } @else if (dataResults().length === 0 && query.length > 0) {
              <div class="px-4 py-8 text-center text-ink-muted">
                <i class="pi pi-database text-3xl opacity-30"></i>
                <p class="text-sm mt-2">{{ 'GLOBAL_SEARCH.NO_DATA_FOUND' | translate }}</p>
              </div>
            }
            @for (result of dataResults(); track result.id; let i = $index) {
              <button
                class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-chrome transition-colors text-left"
                [class.bg-primary-surface]="selectedIndex() === i"
                (click)="navigate(result.route)"
                pRipple>
                <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                     [class.bg-primary-surface]="result.type === 'patient'"
                     [class.bg-success-surface]="result.type === 'invoice'"
                     [class.bg-warning-surface]="result.type === 'appointment'">
                  <i [class]="result.icon + ' text-sm'"
                     [class.text-primary-text]="result.type === 'patient'"
                     [class.text-success-text]="result.type === 'invoice'"
                     [class.text-warning-text]="result.type === 'appointment'"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-ink truncate">{{ result.name }}</p>
                  <p class="text-xs text-ink-muted truncate">{{ result.subtitle }}</p>
                </div>
                <p-tag [value]="result.typeLabel" [severity]="result.tagSeverity" [styleClass]="'!text-[10px] !px-2'"></p-tag>
              </button>
            }
          </p-tabpanel>
          </p-tabpanels>
        </p-tabs>

        <!-- Footer -->
        <div class="border-t border-hairline px-4 py-2 flex items-center justify-between bg-chrome">
          <div class="flex gap-3 text-xs text-ink-muted">
            <span><kbd class="bg-neutral-surface px-1.5 py-0.5 rounded text-xs font-mono">↑↓</kbd> Naviguer</span>
            <span><kbd class="bg-neutral-surface px-1.5 py-0.5 rounded text-xs font-mono">↵</kbd> Ouvrir</span>
            <span><kbd class="bg-neutral-surface px-1.5 py-0.5 rounded text-xs font-mono">ESC</kbd> {{ 'COMMON.CLOSE' | translate }}</span>
          </div>
          <span class="text-xs text-ink-muted">Ctrl K</span>
        </div>
      </div>
    </p-dialog>
  `
})
export class GlobalSearchComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly searchService = inject(GlobalSearchService);
  private searchRequestId = 0;

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  readonly visible = signal(false);
  query = '';
  activeTab: 'all' | 'navigation' | 'data' = 'all';
  readonly searchTabs = ['all', 'navigation', 'data'] as const;
  readonly selectedIndex = signal(0);
  readonly searchLoading = signal(false);

  private searchSubject = new Subject<SearchRequest>();

  // Results signals
  readonly navigationResults = signal<SearchItem[]>([]);
  readonly dataResults = signal<DataResult[]>([]);
  readonly allResults = signal<DataResult[]>([]);

  // Quick access
  readonly quickAccess = computed(() => this.availableNavigationItems().filter(item => [
      '/dashboard',
      '/patients/new',
      '/appointments',
      '/consultations-hub?tab=cabinet',
      '/laboratory-hub?tab=demandes',
      '/billing-hub?tab=factures',
      '/pharmacy-pos',
      '/reception-hub?tab=file'
    ].includes(item.route)
  ));

  // Combined search results
  private readonly patientResults = signal<PatientSearchResult[]>([]);
  private readonly invoiceResults = signal<InvoiceSearchResult[]>([]);
  private readonly appointmentResults = signal<AppointmentSearchResult[]>([]);

  readonly totalResultsStr = computed(() => String(this.allResults().length));

  constructor() {
    this.setupSearch();
  }

  private setupSearch() {
    this.searchSubject.pipe(
      debounceTime(220),
      switchMap(({ query, requestId }) => {
        if (!query || query.length < 2) {
          return of({ requestId, patients: [], invoices: [], appointments: [] });
        }

        return from(Promise.all([
          this.searchService.searchPatients(query),
          this.searchService.searchInvoices(query),
          this.searchService.searchAppointments(query)
        ])).pipe(
          map(([patients, invoices, appointments]) => ({
            requestId,
            patients: patients || [],
            invoices: invoices || [],
            appointments: appointments || []
          })),
          catchError(() => of({ requestId, patients: [], invoices: [], appointments: [] }))
        );
      })
    ).subscribe(({ requestId, patients, invoices, appointments }) => {
      if (requestId !== this.searchRequestId) return;

      this.patientResults.set(patients);
      this.invoiceResults.set(invoices);
      this.appointmentResults.set(appointments);
      this.searchLoading.set(false);
      this.updateDataResults();
      this.updateAllResults();
    });
  }

  onGlobalKeydown(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.visible()) { this.focusSearchInput(true); } else { this.open(); }
    } else if (this.visible() && event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  open() {
    if (this.visible()) {
      this.focusSearchInput(true);
      return;
    }

    this.resetSearch();
    this.visible.set(true);
  }

  close() {
    this.onVisibleChange(false);
  }

  onVisibleChange(visible: boolean) {
    this.visible.set(visible);
    if (!visible) {
      this.searchRequestId++;
      this.searchLoading.set(false);
    }
  }

  onSearch(q: string) {
    const requestId = ++this.searchRequestId;
    this.selectedIndex.set(0);
    this.updateNavigationResults();

    if (q.trim().length < 2) {
      this.clearDataResults();
      this.searchLoading.set(false);
      this.updateAllResults();
      return;
    }

    this.clearDataResults();
    this.searchLoading.set(true);
    this.updateAllResults();
    this.searchSubject.next({ query: q.trim(), requestId });
  }

  onTabChange(tab: string) {
    if (tab === 'all' || tab === 'navigation' || tab === 'data') {
      this.activeTab = tab;
      this.selectedIndex.set(0);
      this.focusSearchInput();
    }
  }

  onKeydown(event: KeyboardEvent) {
    const items = this.getCurrentItems();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex.set(Math.min(this.selectedIndex() + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex.set(Math.max(this.selectedIndex() - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.selectedIndex.set(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.selectedIndex.set(Math.max(items.length - 1, 0));
    } else if (event.key === 'Enter') {
      const item = items[this.selectedIndex()];
      if (item) this.navigate(item.route);
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  navigate(route: string) {
    // `navigate([route])` traite la chaîne entière comme un segment de chemin :
    // un « /reception-hub?tab=salles » atterrissait sur une URL encodée et donc
    // sur la 404. `navigateByUrl` sait lire la query string, ce dont les
    // raccourcis vers un onglet précis d'un hub ont besoin.
    void this.router.navigateByUrl(route);
    this.close();
  }

  private clearResults() {
    this.navigationResults.set([]);
    this.clearDataResults();
  }

  private clearDataResults() {
    this.dataResults.set([]);
    this.allResults.set([]);
    this.patientResults.set([]);
    this.invoiceResults.set([]);
    this.appointmentResults.set([]);
  }

  private updateNavigationResults() {
    if (!this.query.trim()) {
      this.navigationResults.set([]);
      return;
    }

    const terms = this.normalizeSearchText(this.query).split(/\s+/).filter(Boolean);
    const filtered = this.availableNavigationItems().filter(item =>
      terms.every(term => this.normalizeSearchText(
        `${item.label} ${item.description} ${item.category}`
      ).includes(term))
    );
    this.navigationResults.set(filtered.slice(0, 10));
  }

  private updateDataResults() {
    const results: DataResult[] = [];

    // Patient results
    this.patientResults().forEach(p => {
      results.push({
        id: p._id,
        type: 'patient',
        typeLabel: 'Patient',
        name: `${p.firstName} ${p.lastName}`,
        subtitle: p.patientNumber || p.email || p.phone,
        icon: 'pi pi-user',
        tagSeverity: 'success',
        route: `/patients/${p._id}`
      });
    });

    // Invoice results
    this.invoiceResults().forEach(inv => {
      results.push({
        id: inv._id,
        type: 'invoice',
        typeLabel: 'Facture',
        name: `Facture ${inv.invoiceNumber}`,
        subtitle: `${inv.patientName} — ${inv.amount.toLocaleString()} FCFA`,
        icon: 'pi pi-receipt',
        tagSeverity: 'info',
        route: `/billing/${inv._id}`
      });
    });

    // Appointment results
    this.appointmentResults().forEach(appt => {
      results.push({
        id: appt._id,
        type: 'appointment',
        typeLabel: 'RDV',
        name: `RDV avec ${appt.patientName}`,
        subtitle: `${appt.date} à ${appt.time} — ${appt.doctorName}`,
        icon: 'pi pi-calendar',
        tagSeverity: 'warning',
        route: '/appointments'
      });
    });

    this.dataResults.set(results.slice(0, 10));
  }

  private updateAllResults() {
    const results: DataResult[] = [];

    // Add navigation results first
    this.navigationResults().forEach(nav => {
      results.push({
        id: nav.route,
        type: 'navigation',
        typeLabel: 'Navigation',
        name: nav.label,
        subtitle: nav.description,
        icon: nav.icon,
        tagSeverity: 'secondary',
        route: nav.route
      });
    });

    // Add data results
    results.push(...this.dataResults());

    this.allResults.set(results.slice(0, 15));
  }

  private getCurrentItems(): any[] {
    switch (this.activeTab) {
      case 'navigation':
        return this.query.length > 1 ? this.navigationResults() : this.quickAccess();
      case 'data':
        return this.dataResults();
      default:
        return this.query.length > 1 ? this.allResults() : this.quickAccess();
    }
  }

  private availableNavigationItems(): SearchItem[] {
    const role = this.authService.currentUser()?.role;
    return NAVIGATION_ITEMS.filter(item =>
      role === 'super_admin' || (role ? (ROUTE_ACCESS[item.route] ?? ADMIN).includes(role) : false)
    );
  }

  private resetSearch() {
    this.searchRequestId++;
    this.query = '';
    this.activeTab = 'all';
    this.selectedIndex.set(0);
    this.searchLoading.set(false);
    this.clearResults();
  }

  focusSearchInput(select = false) {
    setTimeout(() => {
      const input = this.searchInput?.nativeElement;
      if (!input) return;
      input.focus();
      if (select && input.value) input.select();
    });
  }

  private normalizeSearchText(value: string): string {
    return value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

}

interface SearchRequest {
  query: string;
  requestId: number;
}

interface DataResult {
  id: string;
  type: 'navigation' | 'patient' | 'invoice' | 'appointment';
  typeLabel: string;
  name: string;
  subtitle: string;
  icon: string;
  tagSeverity: 'success' | 'info' | 'warning' | 'secondary' | 'danger';
  route: string;
}
